// ============================================
// Toon Outline Post-Process Effect
// ============================================
// Sobel edge ink: outlines over the scene (cel look) or ink-on-paper sketch.

import type { IRenderContext } from '../../core/IRenderContext';
import {
  PostProcessEffect,
  type PostProcessContext,
  type PostProcessParameter,
} from '../PostProcessStack';
import { OUTLINE_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Outline post-processing effect (Sobel edges)
 */
export class OutlineEffect extends PostProcessEffect {
  readonly type = 'outline';
  readonly displayName = 'Toon Outline';

  // Parameters
  thickness = 1.5;
  threshold = 0.4;
  softness = 0.3;
  mode = 0;            // 0 = outline over scene, 1 = ink on paper
  edgeR = 0.05; edgeG = 0.05; edgeB = 0.08;
  paperR = 0.96; paperG = 0.94; paperB = 0.88;

  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = OUTLINE_LAYOUT.createWriter();

  getParameters(): PostProcessParameter[] {
    return [
      { name: 'thickness', type: 'float', min: 0.5, max: 4, default: 1.5, description: 'Line thickness (px)' },
      { name: 'threshold', type: 'float', min: 0.01, max: 2, default: 0.4, description: 'Edge sensitivity' },
      { name: 'softness', type: 'float', min: 0.01, max: 1, default: 0.3, description: 'Edge softness' },
      { name: 'mode', type: 'int', min: 0, max: 1, default: 0, description: 'Mode (0=outline, 1=sketch)' },
      { name: 'edgeR', type: 'float', min: 0, max: 1, default: 0.05, description: 'Ink R' },
      { name: 'edgeG', type: 'float', min: 0, max: 1, default: 0.05, description: 'Ink G' },
      { name: 'edgeB', type: 'float', min: 0, max: 1, default: 0.08, description: 'Ink B' },
      { name: 'paperR', type: 'float', min: 0, max: 1, default: 0.96, description: 'Paper R (sketch)' },
      { name: 'paperG', type: 'float', min: 0, max: 1, default: 0.94, description: 'Paper G (sketch)' },
      { name: 'paperB', type: 'float', min: 0, max: 1, default: 0.88, description: 'Paper B (sketch)' },
    ];
  }

  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'thickness': return this.thickness;
      case 'threshold': return this.threshold;
      case 'softness': return this.softness;
      case 'mode': return this.mode;
      case 'edgeR': return this.edgeR;
      case 'edgeG': return this.edgeG;
      case 'edgeB': return this.edgeB;
      case 'paperR': return this.paperR;
      case 'paperG': return this.paperG;
      case 'paperB': return this.paperB;
      default: return 0;
    }
  }

  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'thickness': this.thickness = Math.max(0.25, Math.min(8, value)); break;
      case 'threshold': this.threshold = Math.max(0.005, Math.min(4, value)); break;
      case 'softness': this.softness = Math.max(0.005, Math.min(2, value)); break;
      case 'mode': this.mode = Math.floor(Math.max(0, Math.min(1, value))); break;
      case 'edgeR': this.edgeR = Math.max(0, Math.min(1, value)); break;
      case 'edgeG': this.edgeG = Math.max(0, Math.min(1, value)); break;
      case 'edgeB': this.edgeB = Math.max(0, Math.min(1, value)); break;
      case 'paperR': this.paperR = Math.max(0, Math.min(1, value)); break;
      case 'paperG': this.paperG = Math.max(0, Math.min(1, value)); break;
      case 'paperB': this.paperB = Math.max(0, Math.min(1, value)); break;
    }
  }

  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();

    const outlineWgsl = getCompiledShader('postprocess/outline');
    this.shader = ctx.createShader(outlineWgsl, outlineWgsl);

    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'outline-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'outline-ldr',
    });
  }

  protected onRender(context: PostProcessContext): void {
    const { ctx, source, intensity, isHdrTarget } = context;

    // NOTE: PostProcessStack already set render target and viewport

    this.uniformWriter.setAll({
      thickness: this.thickness,
      // Fading the sensitivity out with intensity removes edges gradually.
      threshold: this.threshold / Math.max(intensity, 0.001),
      softness: this.softness,
      mode: this.mode,
      edgeColor: [this.edgeR, this.edgeG, this.edgeB],
      _pad0: 0,
      paperColor: [this.paperR, this.paperG, this.paperB],
      _pad1: 0,
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }

  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
